import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnDestroy
} from '@angular/core';
import { LocaleService } from '@app/core/i18n';
import { CatalogFilter } from '@app/shared/model/interfaces/catalog';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import {
  ContentsRouteParameter,
  CatalogPage,
  CatalogFacetLink,
  ContentsMarketingFilter,
  Deal
} from '@vestiaire/api-sdk-angular';
import { checkRouteParameters, FiltersService } from '@app/shared/components/catalog/services/filters.service';
import { IBaseItem } from '@app/shared/components/catalog/catalog-widgets/catalog-widget';
import { MappingServiceHash } from '../services/mapping.service';
import { environment } from '@env/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { ContentRepositoryService } from '@app/core/services/content/content-repository.service';
import { SizeGuide } from '@app/shared/model/interfaces/sizeGuide/sizeGuide';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SizeGuideModalComponent } from '@app/shared/components/size-guide-modal/size-guide-modal.component';

interface FacetItems {
  [key: string]: number;
}

interface FacetList {
  [key: string]: FacetItems;
}

export const sizeUniverseMatcher = {
  '1': '1',
  '2': '1',
  '3': '1',
  '4': '2',
  '5': '2',
  '6': '3',
  '10': '2',
  '11': '3',
  '12': '1',
  '13': '1',
  '14': '1',
  '16': '2',
  '17': '2',
  '18': '2',
  '19': '2',
  '20': '2',
  '21': '2'
};

@Component({
  selector: 'vc-catalog-filters',
  templateUrl: './catalog-filters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogFiltersComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  nbHits: number;
  @Input()
  facets: FacetList;
  @Input()
  filters: CatalogFilter[];
  @Input()
  refinedFacets: string[];
  @Input()
  page: CatalogPage | null;
  @Input()
  applyPriceFilter: Event;

  @Input()
  facetLink: CatalogFacetLink | null;
  routeParameters: ContentsRouteParameter[];

  @Input()
  showRefinementBody: boolean;

  @Input()
  currentCountry: string;

  @Input()
  userDSeligibility: boolean;

  @Input()
  isMobile: boolean;

  @Output()
  clickedFilter = new EventEmitter<any>();

  @Input()
  catalogContainerOffset: number;

  @Input()
  marketingFilterLocal: ContentsMarketingFilter;

  @Input()
  currentDeal: Deal;

  @Input()
  isLogged: boolean; // user is logged or not

  @Input()
  personalizationIsApplicable: boolean; // if personalization is applicable in this catalog
  @Input()
  isCampaign: boolean; // is a campaign catalog

  public localCountries: {};

  public defaultModalConfig: MatDialogConfig = {
    disableClose: false,
    maxWidth: 600,
    data: {}
  };
  public sizeGuide: SizeGuide | any;
  public sizeLabels: any;
  public sizeStandard: string;
  private ngUnsubscribe = new Subject<void>();

  /**
   * Minimum items for displaying a facet filter.
   */
  private minItems = 2;

  /**
   * Minimum threshold of hits (number of products)
   * for displaying a facet filter.
   *
   * Expressed in percent of total hits.
   */
  private hitsThreshold = 1;
  positionHeader = 0;

  trackByFilter = (index: number) => index;

  isPersonalizationEnable = environment?.feature?.personalization_filter;

  constructor(
    private localeService: LocaleService,
    private platformService: PlatformService,
    private mappingServiceHash: MappingServiceHash,
    private filtersService: FiltersService,
    private transferState: TransferState,
    private contentRepositoryService: ContentRepositoryService,
    private dialogService: MatDialog
  ) {}

  ngOnInit(): void {
    if (typeof this.filters === 'undefined') {
      this.filters = this.filtersService.getDefaultFilters(this.localeService);
    }
    this.setSizeLabels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.facets &&
      changes.facets.currentValue &&
      changes.facets.isFirstChange &&
      this.platformService.isServer
    ) {
      this.mappingServiceHash.init(changes.facets.currentValue);
    }
    if (changes.facets) {
      this.localCountries = this.facets?.localCountries || {};
    }
    if (changes.page && changes.page.currentValue) {
      this.routeParameters = changes.page.currentValue.route.parameters;
    }
  }

  ofType(filter, type): boolean {
    return filter.type === type;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Check if a filter should be hidden.
   *
   * A filter is hidden if its number of items or associated hits
   * don't exceed configured thresholds.
   *
   * @see minItems
   * @see hitsThreshold
   */
  isHiddenFilter(filterOptions: CatalogFilter): boolean {
    let facet: string;

    let isHierarchicalFilter = false;
    if (typeof filterOptions.facet === 'string') {
      facet = filterOptions.facet; // Single-facet Widgets
    } else if (Array.isArray(filterOptions.facet) && filterOptions.facet.length > 0) {
      facet = filterOptions.facet[0]; // Hierarchical Menus
      isHierarchicalFilter = true;
    }

    const items = this.getFacetItems(facet);
    const facetHits = this.getFacetHits(facet);
    const hitsPercentage = this.nbHits && this.nbHits > 0 ? (facetHits / this.nbHits) * 100 : 0;

    let nbItems = items ? Object.keys(items).length : 0;

    if (nbItems > 0 && filterOptions.routeParameters && this.routeParameters) {
      Object.keys(items).forEach(itemKey => {
        const item = { value: itemKey } as IBaseItem;
        if (!checkRouteParameters(filterOptions.routeParameters, this.routeParameters, item)) {
          // Allow showing of depending facet if parent is hidden
          this.refinedFacets.push(facet);

          nbItems--;
        }
      });
    }
    const facetDependency = this.checkFacetDependency(filterOptions);

    if (!filterOptions.forceHide && !facetDependency) {
      /**
       * Show only if filter has items
       */

      return nbItems === 0;
    }

    return (
      (!isHierarchicalFilter && nbItems < this.minItems) ||
      hitsPercentage < this.hitsThreshold ||
      facetDependency ||
      nbItems === 0
    );
  }

  getFacets(): FacetList {
    return this.facets || {};
  }

  hasFacet(facet: string): boolean {
    return facet in this.getFacets();
  }

  getFacetItems(facet: string): FacetItems {
    let items;

    if (this.hasFacet(facet)) {
      items = this.getFacets()[facet];
    }
    return items || {};
  }

  getFacetHits(facet: string): number {
    let nbHits = 0;
    const items = this.getFacetItems(facet);

    for (const item of Object.keys(items)) {
      nbHits += items[item];
    }
    return nbHits;
  }

  scrollTop() {
    if (this.platformService.isBrowser) {
      window.scrollTo(0, this.catalogContainerOffset - 132);
    }
  }

  /**
   *  Check if facet depends on others facets (true => filter hidden)
   * @param filterOptions
   */
  checkFacetDependency(filterOptions: CatalogFilter): boolean {
    if (!filterOptions.requiredFacet) {
      return false;
    }

    if (!this.refinedFacets) {
      return true;
    }
    const requiredFacets = Array.isArray(filterOptions.requiredFacet)
      ? filterOptions.requiredFacet
      : [filterOptions.requiredFacet];

    let nbFacet = 0;
    requiredFacets.map(requiredFacet => {
      nbFacet += this.refinedFacets.indexOf(requiredFacet) !== -1 ? 1 : 0;
    });

    return nbFacet !== requiredFacets.length;
  }

  clickFilter(event) {
    this.clickedFilter.emit(event);
  }

  getLocalCountriesList() {
    return this.facets?.localCountries || [''];
  }
  setSizeLabels() {
    const key = makeStateKey('sizeLabels');
    if (this.transferState.hasKey(key)) {
      const datas = this.transferState.get(key, null);
      this.sizeLabels = datas?.categorySizeStandard;
      this.sizeStandard = datas?.categorySizeStandard;
    } else {
      this.contentRepositoryService
        .getProductSalesCondition()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          res => {
            this.transferState.set(key, res);
            this.sizeLabels = res?.categorySizeStandard;
            this.sizeStandard = res?.sizeStandard;
          },
          error => {
            console.error('No datas from ProductSalesCondition', error);
          }
        );
    }
  }

  launchSizeGuide(event) {
    const key = makeStateKey('sizeGuide');
    if (this.transferState.hasKey(key)) {
      const datas = this.transferState.get(key, null);
      this.sizeGuide = datas;
      this.openSizeGuideModal(event, datas);
    } else {
      this.contentRepositoryService
        .getSizeGuide()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          res => {
            this.transferState.set(key, res);
            this.openSizeGuideModal(event, res);
          },
          error => {
            console.error('No datas from ProductSalesCondition', error);
          }
        );
    }
  }

  openSizeGuideModal(sizeId: string, sizeGuideJson: any) {
    const sizeGuide = this.setSizeGuide(sizeId, sizeGuideJson);
    this.dialogService.open(SizeGuideModalComponent, {
      maxWidth: 800,
      data: {
        size: sizeId,
        standard: this.sizeStandard,
        sizeGuide: sizeGuide
      }
    });
  }

  setSizeGuide(categoryId, sizeGuideJson) {
    const sizeGuide = {
      title: '',
      guide: []
    };
    const universeId = sizeUniverseMatcher[categoryId];
    if (universeId && sizeGuideJson[universeId]) {
      sizeGuideJson[universeId]['categories'].map((category: any) => {
        if (category['sizeType'] === categoryId) {
          sizeGuide.guide = category['content']['guide'];
          sizeGuide.title = category['content']['title'];
        }
      });
    }
    return sizeGuide;
  }
}
